import { computed } from "vue";
import { formatNumber } from "@/helpers/functions/NumberHelpers";
import {
  COVERAGE_STATUS,
  getTerritoryCoverage,
} from "@/modules/oportunidades/domain/territorySelection";

function formatCount(count, singular, plural) {
  const label = count === 1 ? singular : plural;
  return `${formatNumber(count)} ${label}`;
}

function getRegionName(region) {
  return region?.nome || region?.regiao || "";
}

export function useTerritorySelectionPresentation(options) {
  const {
    filteredMunicipalities,
    filteredRegions,
    filteredStates,
    isError,
    isMunicipalitySearchActive,
    isSelected,
    loading,
    modelValue,
    regions,
    selectedRegion,
    selectedState,
    territoryCoverage,
  } = options;

  const selectedRegionCount = computed(
    () =>
      regions.value.filter((region) => {
        const coverage = getTerritoryCoverage(
          territoryCoverage.value,
          "regiao",
          region.regiao
        );

        return coverage.status === COVERAGE_STATUS.COMPLETE;
      }).length
  );
  const selectedStateCount = computed(() => modelValue.value.estados.length);
  const selectedMunicipalityCount = computed(
    () => territoryCoverage.value.effectiveMunicipalityValues.size
  );
  const hasSelectedValue = computed(
    () =>
      modelValue.value.estados.length > 0 ||
      modelValue.value.municipios.length > 0
  );
  const selectionSummaryText = computed(() => {
    const regionLabel = formatCount(
      selectedRegionCount.value,
      "região",
      "regiões"
    );
    const stateLabel = formatCount(
      selectedStateCount.value,
      "estado",
      "estados"
    );
    const municipalityLabel = formatCount(
      selectedMunicipalityCount.value,
      "município",
      "municípios"
    );

    return `${regionLabel} · ${stateLabel} · ${municipalityLabel}`;
  });

  const stateTitle = computed(() => {
    if (!selectedRegion.value) return "Estados";

    return `Estados do ${getRegionName(selectedRegion.value)}`;
  });
  const municipalityTitle = computed(() => {
    if (isMunicipalitySearchActive.value) {
      return "Buscando em todo o Brasil";
    }

    if (selectedState.value) {
      return `Municípios de ${selectedState.value.sigla}`;
    }

    if (selectedRegion.value) {
      return `Municípios do ${getRegionName(selectedRegion.value)}`;
    }

    return "Municípios Selecionados";
  });
  const municipalityEmptyMessage = computed(() => {
    const hasActiveContext = selectedRegion.value || selectedState.value;

    return !hasActiveContext && !isMunicipalitySearchActive.value
      ? "Nenhum município selecionado."
      : "Nenhum município encontrado.";
  });

  const showColumnCounters = computed(() => !loading.value && !isError.value);
  const regionFooterText = computed(() => {
    const visibleRegions = filteredRegions.value;
    const completeRegions = visibleRegions.filter((region) => {
      const coverage = getTerritoryCoverage(
        territoryCoverage.value,
        "regiao",
        region.regiao
      );

      return coverage.status === COVERAGE_STATUS.COMPLETE;
    }).length;

    return `${formatNumber(completeRegions)} de ${formatCount(
      visibleRegions.length,
      "região",
      "regiões"
    )}`;
  });
  const stateFooterText = computed(() => {
    const visibleStates = filteredStates.value;
    const completeStates = visibleStates.filter((state) => {
      const coverage = getTerritoryCoverage(
        territoryCoverage.value,
        "estado",
        state.sigla
      );

      return coverage.status === COVERAGE_STATUS.COMPLETE;
    }).length;

    return `${formatNumber(completeStates)} de ${formatCount(
      visibleStates.length,
      "estado",
      "estados"
    )}`;
  });
  const municipalityFooterText = computed(() => {
    const isSummaryMode =
      !isMunicipalitySearchActive.value &&
      !selectedRegion.value &&
      !selectedState.value;

    if (isSummaryMode) {
      return formatCount(
        selectedMunicipalityCount.value,
        "município",
        "municípios"
      );
    }

    const visibleMunicipalities = filteredMunicipalities.value;
    const coveredMunicipalities = visibleMunicipalities.filter((municipality) =>
      territoryCoverage.value.effectiveMunicipalityValues.has(municipality.id)
    ).length;

    return `${formatNumber(coveredMunicipalities)} de ${formatCount(
      visibleMunicipalities.length,
      "município",
      "municípios"
    )}`;
  });

  function isIndeterminate(level, value) {
    const coverage = getTerritoryCoverage(
      territoryCoverage.value,
      level,
      value
    );

    return coverage.status === COVERAGE_STATUS.PARTIAL;
  }

  function coverageLabel(level, value) {
    const coverage = getTerritoryCoverage(
      territoryCoverage.value,
      level,
      value
    );
    if (coverage.status !== COVERAGE_STATUS.PARTIAL) return "";

    return `${formatNumber(coverage.selected)}/${formatNumber(coverage.total)}`;
  }

  function selectionActionLabel(level, value) {
    return isSelected(level, value) ? "square-check" : "fa-regular fa-square";
  }

  function toggleSelectionLabel(level, value, label) {
    const action = isSelected(level, value) ? "Remover" : "Selecionar";
    return `${action} ${label}`;
  }

  return {
    hasSelectedValue,
    municipalityEmptyMessage,
    municipalityFooterText,
    municipalityTitle,
    regionFooterText,
    selectionSummaryText,
    showColumnCounters,
    stateFooterText,
    stateTitle,

    coverageLabel,
    isIndeterminate,
    selectionActionLabel,
    toggleSelectionLabel,
  };
}
